import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Tag, Lock } from "lucide-react";
import { toast } from "sonner";

const TYPE_TABS = [
  { id: "expense", label: "Expense", accent: "#E63946" },
  { id: "income", label: "Income", accent: "#4C956C" },
  { id: "saving", label: "Saving", accent: "#E07A5F" },
];

const COLOR_SWATCHES = [
  "#E07A5F", "#4C956C", "#2F4F4F", "#E63946", "#6B4E71",
  "#D4A017", "#5C5C5C", "#7B9EA8", "#B4614E", "#3D5A80",
];

const EMOJI_SUGGESTIONS = [
  "🏷", "🍽", "🚗", "🧾", "🛍", "🎬", "💊", "📦", "💼", "💻",
  "📈", "🎁", "💰", "🛟", "🌴", "✈", "🎯", "💵", "🏠", "📚",
  "⚡", "☕", "🎮", "🎵", "👕", "🐾", "💡", "🍺", "🏋", "✂",
];

export default function Categories() {
  const [grouped, setGrouped] = useState({ expense: [], income: [], saving: [] });
  const [activeTab, setActiveTab] = useState("expense");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // category object or null
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ name: "", type: "expense", icon: "🏷", color: "#5C5C5C" });

  const load = async () => {
    const { data } = await api.get("/categories");
    setGrouped(data);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", type: activeTab, icon: "🏷", color: "#5C5C5C" });
    setOpen(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({ name: cat.name, type: cat.type, icon: cat.icon, color: cat.color });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/categories/${editing.id}`, {
          name: form.name.trim(),
          icon: form.icon,
          color: form.color,
        });
        toast.success("Category updated");
      } else {
        await api.post("/categories", {
          name: form.name.trim(),
          type: form.type,
          icon: form.icon,
          color: form.color,
        });
        toast.success("Category added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save category");
    }
  };

  const remove = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/categories/${deleteId}`);
      toast.success("Category deleted");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
      setDeleteId(null);
    }
  };

  const list = grouped[activeTab] || [];
  const deleteTarget = list.find((c) => c.id === deleteId);

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Personalize</div>
          <h1 className="font-display text-4xl mt-2">Categories</h1>
          <p className="text-sm text-[var(--muted-text)] mt-1">
            Add your own categories with icons and colors. Defaults are locked but can be renamed.
          </p>
        </div>
        <Button
          data-testid="add-category-btn"
          onClick={openCreate}
          className="rounded-full bg-[#E07A5F] hover:bg-[#c9694f] text-white h-11 px-5"
        >
          <Plus className="w-4 h-4 mr-2" /> New category
        </Button>
      </div>

      <div className="flex gap-2 mb-6" data-testid="category-type-tabs">
        {TYPE_TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`category-tab-${t.id}`}
            onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${
              activeTab === t.id
                ? "bg-[#2F4F4F] text-[#FDFCF8]"
                : "bg-[var(--hover)] text-[var(--muted-text)] hover:text-[var(--text)]"
            }`}
          >
            {t.label}
            <span className="ml-2 text-xs opacity-70">{(grouped[t.id] || []).length}</span>
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <div data-testid="categories-empty" className="card-flat p-12 text-center">
          <Tag className="w-8 h-8 mx-auto text-[var(--muted-text)]" />
          <h3 className="font-display text-xl mt-3">No categories yet</h3>
          <p className="text-sm text-[var(--muted-text)] mt-1">Add one to organize your money.</p>
        </div>
      ) : (
        <div data-testid="categories-list" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.map((c) => (
            <div
              key={c.id}
              data-testid={`category-row-${c.name}`}
              className="card-flat p-4 flex items-center gap-3 hover-lift"
            >
              <div
                className="w-11 h-11 rounded-md flex items-center justify-center text-lg shrink-0"
                style={{ backgroundColor: `${c.color}22`, color: c.color }}
              >
                {c.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{c.name}</div>
                <div className="text-xs text-[var(--muted-text)] flex items-center gap-1">
                  {c.is_default ? (
                    <><Lock className="w-3 h-3" /> Default</>
                  ) : (
                    "Custom"
                  )}
                </div>
              </div>
              <Button
                data-testid={`category-edit-${c.name}`}
                size="icon"
                variant="ghost"
                onClick={() => openEdit(c)}
                aria-label="Edit category"
              >
                <Pencil className="w-4 h-4" />
              </Button>
              <Button
                data-testid={`category-delete-${c.name}`}
                size="icon"
                variant="ghost"
                onClick={() => setDeleteId(c.id)}
                disabled={c.is_default}
                aria-label="Delete category"
                title={c.is_default ? "Default categories cannot be deleted" : "Delete"}
              >
                <Trash2 className={`w-4 h-4 ${c.is_default ? "opacity-30" : "text-[#E63946]"}`} />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit category" : "New category"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="flex items-center gap-3">
              <div
                className="w-14 h-14 rounded-md flex items-center justify-center text-2xl shrink-0"
                style={{ backgroundColor: `${form.color}22`, color: form.color }}
              >
                {form.icon}
              </div>
              <div className="flex-1 space-y-2">
                <Label>Name</Label>
                <Input
                  data-testid="category-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Coffee, Side hustle"
                  required
                  maxLength={40}
                />
              </div>
            </div>

            {!editing && (
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={form.type}
                  onValueChange={(v) => setForm({ ...form, type: v })}
                >
                  <SelectTrigger data-testid="category-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="saving">Saving</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="category-icon-grid">
                {EMOJI_SUGGESTIONS.map((e) => (
                  <button
                    key={e}
                    type="button"
                    data-testid={`icon-${e}`}
                    onClick={() => setForm({ ...form, icon: e })}
                    className={`w-9 h-9 rounded-md text-lg flex items-center justify-center transition-colors ${
                      form.icon === e ? "bg-[#2F4F4F] text-[#FDFCF8]" : "bg-[var(--hover)] hover:bg-[var(--track)]"
                    }`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2" data-testid="category-color-grid">
                {COLOR_SWATCHES.map((color) => (
                  <button
                    key={color}
                    type="button"
                    data-testid={`color-${color}`}
                    onClick={() => setForm({ ...form, color })}
                    aria-label={`Choose color ${color}`}
                    className={`w-8 h-8 rounded-full transition-transform ${
                      form.color === color ? "ring-2 ring-offset-2 ring-[#2F4F4F] scale-110" : ""
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                data-testid="category-submit"
                type="submit"
                disabled={!form.name.trim()}
                className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
              >
                {editing ? "Save changes" : "Add category"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleteTarget?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Existing transactions with this category will keep their label — they just won&apos;t appear as a preset anymore.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="category-delete-confirm"
              onClick={remove}
              className="bg-[#E63946] hover:bg-[#c92e3b] text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
